import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { User } from 'oidc-client-ts';
import { getUser, login, logout, userManager } from '../config/oidcConfig';

// Define the shape of our authentication context
interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  error: Error | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  error: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  login: async () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  logout: async () => {},
  isAuthenticated: false,
});

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Props for the AuthProvider component
interface AuthProviderProps {
  children: ReactNode;
}

// AuthProvider component to wrap the application
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [isUserAuthenticated, setIsUserAuthenticated] = useState<boolean>(false);

  // Load the user on initial render
  useEffect(() => {
    const loadUser = async () => {
      try {
        setIsLoading(true);
        const currentUser = await getUser();
        setUser(currentUser);
        setIsUserAuthenticated(!!currentUser && !currentUser.expired);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load user'));
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();

    // Set up event listeners for user events
    const handleUserLoaded = (user: User) => {
      setUser(user);
      setIsUserAuthenticated(true);
    };

    const handleUserUnloaded = () => {
      setUser(null);
      setIsUserAuthenticated(false);
    };

    const handleSilentRenewError = (error: Error) => {
      setError(error);
    };

    const handleUserSignedIn = () => {
      getUser().then(currentUser => {
        setUser(currentUser);
        setIsUserAuthenticated(!!currentUser && !currentUser.expired);
      });
    };

    const handleUserSignedOut = () => {
      setUser(null);
      setIsUserAuthenticated(false);
    };

    // Register event handlers
    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addUserUnloaded(handleUserUnloaded);
    userManager.events.addSilentRenewError(handleSilentRenewError);
    userManager.events.addUserSignedIn(handleUserSignedIn);
    userManager.events.addUserSignedOut(handleUserSignedOut);

    // Clean up event listeners on unmount
    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
      userManager.events.removeSilentRenewError(handleSilentRenewError);
      userManager.events.removeUserSignedIn(handleUserSignedIn);
      userManager.events.removeUserSignedOut(handleUserSignedOut);
    };
  }, []);

  // Context value
  const value: AuthContextType = {
    user,
    isLoading,
    error,
    login,
    logout,
    isAuthenticated: isUserAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
